export interface IPreview {
  title: string;
  subtitle: string;
  content: string[];
  imageUrl: string;
}

export interface IInfoBlock {
  title: string;
  blocks: {
    title: string;
    sticker: string;
    textPoints: string[];
  }[];
}

export interface IStatistics {
  title: string;
  teams: {
    title: string;
    points: string[];
  }[];
  points: {
    mainPoint: string;
    secondPoint: string;
  }[];
}

export interface IOurTask {
  title: string;
  description: string[];
  cards: {
    image: string;
    description: string;
  }[];
}

export interface IFaqItem {
  question: string;
  answers: string[];
}

export interface IFaq {
  title: string;
  items: IFaqItem[];
  hoverColor: string;
}

export interface ITechnologyItem {
  image: string;
  name: string;
  width: number;
  height: number;
}

export interface ITechnologyStack {
  title: string;
  items: ITechnologyItem[];
}

export interface IGivingliState {
  preview: IPreview;
  infoBlock: IInfoBlock;
  statistics: IStatistics;
  ourTask: IOurTask;
  greeting: IGreetingSection;
  faq: IFaq;
  technologyStack: ITechnologyStack[];
  feedback: IFeedback;
  appStore: IAppStore;
}

export interface IAppStore {
  title: string;
  image: string;
}

export interface IFeedback {
  title: string;
  quoteSrc: string;
  comment: string;
  authorName: string;
  authorAvatar: string;
  authorSpecialist: string;
}

export interface IGreetingCard {
  title: string;
  text: string | string[];
  image: string;
}

export interface IGreetingSection {
  title: string;
  cards: IGreetingCard[];
}

const imgBasePath = "/givingli-images";

export const givingliState: IGivingliState = {
  preview: {
    title: "Givingli",
    subtitle: "Your best greeting card maker app",
    content: [
      "Givingli is US-based company that transforms the general user gifting experience. It offers clients chances to send greeting cards and enclose gift certificates.",
      "With the dedication to its value of user-centricity, Givingli aims to maximize the extent of customization by collaborating with multiple creators who craft unique and authentic templates to please the requests of the most demanding clients.",
      "The cooperation with 50+ more organizations, including Sephora, Uber, Starbucks, iTunes, etc., enables Givingli users to send versatile gift certificates to bring their loved ones, friends, or colleagues a truly memorable greeting experience.",
    ],
    imageUrl: `${imgBasePath}/preview.png`,
  },
  infoBlock: {
    title: "Givingli’s essence",
    blocks: [
      {
        title: "Givingli’s current objectives",
        sticker: `${imgBasePath}/stickers/fingers-sticker.png`,
        textPoints: [
          "Referral programs: when the user invites their friends and can receive bonuses.",
          "Scheduling cards: when the client can book a date in advance to congratulate the person.",
          "The diversified subscription model: which aims to cover the needs of pro users, such as enterprises and organizations.",
        ],
      },
      {
        title: "What makes Givingli unique?",
        sticker: `${imgBasePath}/stickers/love-sticker.png`,
        textPoints: [
          "The opportunity to add a gift card. The user may attach a gift certificate of famous brands to the greeting card.",
          "Original design: the chance to choose and edit an envelope for your card.",
          "More customization options. You can create and customize birthday or Christmas cards online, make them personalized.",
        ],
      },
      {
        title: "Markers of Givingli’s success",
        sticker: `/givingli-images/stickers/rainbow-sticker.png`,
        textPoints: [
          "Givingli Event: the element that economizes time and money by enabling users to check who from the invited list is going to attend their event.",
          "Givingli Cash: a unique in-app wallet system that enables clients to gift, keep, and redeem their gift certificates whenever they want.",
          "The auto-scaling system that actively works on holidays (see below for details).",
          "A stable product nominated by Apple on Today’s and Developer’s insights during holiday seasons.",
        ],
      },
    ],
  },
  statistics: {
    title: "Our professional collaboration",
    teams: [
      {
        title: "Agilie’s team",
        points: [
          "iOS developers - 2",
          "Back-End Developers - 2",
          "Front-End Developers - 3",
          "Quality Assurance Specialists - 3",
          "Project Manager - 1",
          "Business Analyst - 1",
          "Android Developers - 2",
        ],
      },
      {
        title: "Givingli’s team",
        points: [
          "CEO",
          "Product Owner",
          "Designers Team",
          "Customer Support Team",
          "Marketing Team",
        ],
      },
    ],
    points: [
      {
        mainPoint: "Dedicated team",
        secondPoint: "Service model",
      },
      {
        mainPoint: "10M+",
        secondPoint: "Application installs",
      },
      {
        mainPoint: "$1M+",
        secondPoint: "Budget",
      },
      {
        mainPoint: "10X",
        secondPoint: "Traffic growth past year",
      },
    ],
  },
  ourTask: {
    title: "Our tasks",
    description: [
      "Agilie takes full responsibility for the technical development and implementation of the project’s features, ensuring their maximized alignment with Givingli’s business objectives.",
      "We elaborate on development and maintenance of the system’s architecture, accentuating on scalability and seamless feature integration to maintain the platform’s evolving requirements.",
      "By implementing the dedicated team model of outsourcing, our professionals work on the Agile methodology by providing the client with regular product updates via the short iterative development cycles, remaining open to alterations. ",
      "Since 2023, Agilie has kept delivering the comprehensive scope of Givingli’s business-driven functionalities, including but not limited to:",
    ],
    cards: [
      {
        image: `${imgBasePath}/gear.png`,
        description:
          "Multi-platform compatibility for the versatile spectrum of all design features, supporting their seamless customization.",
      },
      {
        image: `${imgBasePath}/wallet.png`,
        description:
          "Engineering Givingli Cash, an inside-app virtual wallet system that enables users to operate gift certificates. Users can cancel certificates, retain and amass the funds in a virtual balance, and later apply them to the purchase they need.",
      },
      {
        image: `${imgBasePath}/security.png`,
        description:
          "Implementation of a solid suit of anti-fraud prevention strategies to maximize security against DDoS attacks.",
      },
      {
        image: `${imgBasePath}/apple.png`,
        description:
          "Crafting the scheduling logic for greeting date selection and promoted its functionality via real-time synchronization between the Web and iOS.",
      },
      {
        image: `${imgBasePath}/android.png`,
        description:
          "Developing and setting the Android app, ensuring its full alignment with system architecture.",
      },
    ],
  },
  faq: {
    title: "Development challenges to overcome",
    items: [
      {
        question: "High load",
        answers: [
          "As the application's primary mission is to provide users with the ability to send customized greeting cards and gift certificates, peak usability occurs during the most popular celebration days, such as Christmas and Mother’s Day. The heightened demand during these high-traffic periods led to an increase in load that caused performance bottlenecks. As an outcome, this provoked significant slowdowns in the system’s functionality",
          "<strong>Implemented solution</strong>",
          "To resolve the issue, we took the following actions:",
          "<ul> <li> Firstly, the database management was performed by optimizing the complex queries. <li> Indexing and horizontal scaling of the database were implemented to handle large number of queries. Our developers integrated the database-level caching and utilized Redis to lower the load.</li> <li>Subsequently, Sidekiq and Redis were applied to perform long and resource-intensive operations in the background (Background Jobs), which provided a quick response for users.</li> <li>The third step was read/write database partitioning through Ruby on Rails. The separation of the databases for the presented functions aimed at balancing the load. </li> <li>We performed load testing and, by simulating the high pressure, identified and overcame weaknesses in the system. This set of actions enabled us to maintain the correct functionality of all three of Givingli’s platforms during the peak dates.</li></ul>",
        ],
      },
      {
        question: "Multi-platform support",
        answers: [
          "The Givingli ecosystem consists of three platforms: web software, an iOS app, and an Android application, which elucidated the challenges of multi-platform software maintenance, as it required a sync in feature delivery and seamless interoperability between the platforms.",
          "<strong>Implemented solution</strong>",
          "Our developers implemented the API versioning approach, enabling the creation of a consistent experience for users of all platforms. It also enabled the independent implementation of the updates by the platforms without any threat to system integrity as a whole.",
          "<ul> <li> API versioning was crucial to maintain backward compatibility, as customers could still use older versions while new features were added.</li> <li>The presented solution was pivotal as it enabled the implementation of the new digital elements by preserving integrity without breaking the existing logic of the platform.</li> <li>Up-to-date automatic documentation optimized developers' interaction with the API.</li> <li>Our final solution was checking and testing that various environments worked properly. This was important as the versatility of the platform required proper coordination. This was the reason why we applied integration testing and QA manual testing.</li></ul>",
        ],
      },
      {
        question: "App responsiveness & high level of user experience",
        answers: [
            "The client wanted to focus the content of their greeting card app development on being more diversified and delivered through unique and aesthetic design elements. From a technical perspective, this brought the challenge of managing the rendering of dynamic content while supporting high-level application performance, user experience, and app responsiveness.",
            "<strong>Implemented solution</strong>",
            "We used WebSockets to promote and maintain the real-time functionality of the live chat and notification systems so that the clients could enjoy interactivity across three platforms.",
            "Standard libraries for animations and design did not consistently provide the required optimization level to support high-quality of user experience and app responsiveness. Thus, in order to achieve the best user experience, Agilie crafted a set of custom solutions to focus on enhancing the application response time and streamlining operations to grant seamless performance. The following is a couple of illustrative examples.",
            "<strong><li>Custom card builder</strong></li>",
            "We crafted a custom card builder, a tool utilized for creating and designing personalized gift cards with text, video, and audio components to make individualized greetings. Initially, the Konva.js library was applied to cover the aforementioned functionalities. However, its lack of optimization for multi-platform support made it challenging to maintain the functionality of those design patterns, as by that time, Givingli had an iOS app and Web platform.",
            "The challenge above made us transition to the HTML Editable Component as it provided the opportunity to embed the HTML tags directly to the input field. This solution streamlined the flexibility in customizing non-trivial, individualized designs, videos, and audio materials, by optimizing user experience and application responsiveness.",
            "<strong><li>Content rendering</strong></li>",
            "While elaborating on Givingli’s custom calendar, Agilie faced the dilemma of content saturation. The calendar displayed events in a one-month range but included two additional rows above and below the visible month. The upper row kept data for the preceding months, while the lower reflected content elements for the upcoming months.",
            "We encountered a substantial rise in the rendered content elements when scrolling through multiple months, mainly for a six-month range. This led to performance bottlenecks, represented by the performance lag and delayed responsiveness in the calendar’s functionality.",
            "As it handled the large dataset that was rendered inside the scrollable container, our team worked on the virtual scroll approach. Virtual scroll rendered only the visible elements in the viewport. This optimization effectively reduced the number of DOM elements with optimized memory allocation.",
            "The virtual scroll removed out-of-view DOM elements and added the new ones that emerged in the viewport. This content-loading approach eliminated lagging and improved software responsiveness and performance, which boosted user experience."
          ]
      },
      {
        question: "Security challenges",
        answers: [
          "The Givingli attracts multiple clients through its versatile subscription models, offering personalized gifting certificates and making the system a target for fraud requests and DDoS attacks.",
          "<strong>Implemented solution</strong>",
          "Agilie implemented the maximum number of security measures, including:",
          "<ul><li>Rate limitingwas applied to restrict the number of requests a single user could make within a specific time, as this was considered a vector of potential cyberattack.</li> <li>Firewall to block unauthorized access to open ports, which secured the system from intrusion.</li> <li>Authentication and authorizationwere intensified to restrict access. Key encryption was used to protect sensitive data.</li> <li>The implementation of New Relic was pivotal in monitoring the high request volumes and receiving alerts on suspicious user activities, including multiple attempts to log in within a short timeframe.</li> <li>New Relic analyzed the system under load by enabling the detection of manipulative behavior and taking counter-measures.</li></ul>",
        ]
      }
    ],
    hoverColor: "gray",
  },
  technologyStack: [
    {
      title: "Cloud solutions",
      items: [
        {
          image: `${imgBasePath}/icons/aws.png`,
          name: "AWS",
          width: 56,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/s3.png`,
          name: "S3 buckets",
          width: 34,
          height: 40,
        },
      ],
    },
    {
      title: "Technologies and frameworks",
      items: [
        {
          image: `${imgBasePath}/icons/angular.png`,
          name: "Angular",
          width: 38,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/rails.png`,
          name: "Ruby on Rails",
          width: 70,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/avfoundation.png`,
          name: "AVFoundation",
          width: 40,
          height: 40,
        },
      ],
    },
    {
      title: "Databases",
      items: [
        {
          image: `${imgBasePath}/icons/postgresql.png`,
          name: "Postgresql",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/mysql.png`,
          name: "MySQL",
          width: 72,
          height: 22,
        },
        {
          image: `${imgBasePath}/icons/redis.png`,
          name: "Redis",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/realm.png`,
          name: "Realm",
          width: 40,
          height: 40,
        },
      ],
    },
    {
      title: "Security and crash reporting",
      items: [
        {
          image: `${imgBasePath}/icons/firewall.png`,
          name: "Firewall",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/sentry.png`,
          name: "Sentry",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/firebase.png`,
          name: "Firebase Crashlytics",
          width: 36,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/newrelic.png`,
          name: "New Relic",
          width: 34,
          height: 40,
        },
      ],
    },
    {
      title: "Programming languages",
      items: [
        {
          image: `${imgBasePath}/icons/kotlin.png`,
          name: "Kotlin",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/swift.png`,
          name: "Swift",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/objective-c.png`,
          name: "Objective-C",
          width: 28,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/typescript.png`,
          name: "TypeScript",
          width: 40,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/ruby.png`,
          name: "Ruby",
          width: 40,
          height: 40,
        },
      ],
    },
    {
      title: "Integrations",
      items: [
        {
          image: `${imgBasePath}/icons/stripe.png`,
          name: "Stripe",
          width: 70,
          height: 30,
        },
        {
          image: `${imgBasePath}/icons/applepay.png`,
          name: "Apple Pay",
          width: 70,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/amazonapi.png`,
          name: "Amazon API",
          width: 34,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/gyft.png`,
          name: "Gyft",
          width: 60,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/googleanalytics.png`,
          name: "Google Analytics",
          width: 36,
          height: 40,
        },
        {
          image: `${imgBasePath}/icons/segmentanalytics.png`,
          name: "Segment Analytics",
          width: 40,
          height: 40,
        },
      ],
    },
  ],
  greeting: {
    title: "Key features",
    cards: [
      {
        title: "Greeting cards gallery",
        text: "Givingli provides users with a choice of a huge number of unique electronic greeting cards for any occasion. Everyone will be able to find and pick a picture that best suits a particular holiday.",
        image: "/givingli-images/iphone-1.png",
      },
      {
        title: "Custom greeting cards",
        text: "The feature implies that you can create and design your own birthday card, making it unique, different from everything else. The proposed customization options include the possibility to supplement the card with your ideas, sign its reverse side, choose a gift certificate and a virtual envelope, and much more.",
        image: "/givingli-images/iphone-2.png",
      },
      {
        title: "Flexible gift certificates",
        text: [
          "This is a very special app feature. It provides a great opportunity to please your loved ones not only with a greeting card but with a gift certificate from a popular store or service attached to it. ",
          "Givingli works with wide-known brands such as Adidas, Airbnb, Uber, Sephora, iTunes, and more than 50 other no less illustrious names. An important point: it's up to you to decide how much money to put on a gift card, there are no fixed sums.",
        ],
        image: "/givingli-images/iphone-3.png",
      },
      {
        title: "Schedule logic for card sending",
        text: [
          "This feature allows clients to schedule a specific date for sending their greeting card or gift certificate.",
        ],
        image: "/givingli-images/iphone-4.png",
      },
      {
        title: "Pro features",
        text: [
          "Pro features represent a subscription model that covers the interests of businesses and enterprises.",
          "Exclusive design content and brand kit with unique templates, design, and visual elements can reinforce the power of company’s branding.",
          "This best greeting card app for iOS provides organizations with powerful opportunities that can assist in congratulating employees and categorizing gift-relevant expenses.",
        ],
        image: "/givingli-images/iphone-5.png",
      },
      {
        title: "Givingli Event",
        text: [
          "Givingli Event is a feature that enables users to create and schedule dates on specific occasions. The possibilities range from customization of digital invitations and inclusion of guests to indicating preferences about the gifts. The invited guests can view the gift requests and offer personal options.",
        ],
        image: "/givingli-images/iphone-6.png",
      },
    ],
  },
  appStore: {
    title: "Get the app",
    image: "/givingli-images/appstore-button.svg",
  },
  feedback: {
    title: "Client’s feedback",
    quoteSrc: "/mad-id-images/icons/quotes.png",
    comment:
      "Agilie was great at taking direction and the feedback on user testing has been amazing so far. They’re a cost-effective and responsive team that communicates daily and delivers on time. They have a friendly disposition and are passionate about the work.",
    authorName: "Ben Green",

    authorAvatar: `/givingli-images/author-avatar.png`,
    authorSpecialist: "Givingli, Founder",
  },
};
